import React, { useState, useEffect } from "react";
import FileUploadList from "./FileUploadList";
import CircularProgress from "./CircularProgress";

const UploadingInvoices = ({
  totalInvoices = 5,
  totalInvoiceItems = [],
  processedInvoices = 2,
  timeElapsed,
}) => {
  const [progress, setProgress] = useState(0);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [processedFileSize, setProcessedFileSize] = useState(0);

  useEffect(() => {
    // Calculate total file size
    const totalSize = totalInvoiceItems.reduce((sum, item) => {
      // Extract numeric value from size string (e.g., "2.5 MB")
      const sizeValue = parseFloat(item.size);
      return sum + (isNaN(sizeValue) ? 0 : sizeValue);
    }, 0);
    setTotalFileSize(totalSize);

    // Calculate actual combined progress from individual item progress values
    // instead of just using the count of processed files
    const combinedProgress = totalInvoiceItems.reduce((sum, item) => {
      return sum + (item.progress || 0);
    }, 0);

    // Get the average progress across all items
    const averageProgress = combinedProgress / (totalInvoiceItems.length || 1);

    // Set the overall progress based on the average progress of all items
    setProgress(averageProgress);

    // Calculate processed file size based on the actual combined progress
    // instead of just using the count of processed files
    const processedSize = (averageProgress / 100) * totalSize;
    setProcessedFileSize(processedSize);
  }, [totalInvoiceItems, processedInvoices, totalInvoices]);

  const formatFileSize = (size) => {
    return size.toFixed(2);
  };

  const formatElapsedTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds} seconds`;
  };

  // Calculate counts
  const completedItems = totalInvoiceItems.filter(item => item.progress === 100).length;
  const processingItems = totalInvoiceItems.filter(item => item.progress > 0 && item.progress < 100).length;
  const pendingItems = totalInvoiceItems.filter(item => item.progress === 0 && item.status !== "Failed").length;
  const failedItems = totalInvoiceItems.filter(item => item.status === "Failed").length;

  return (
    <div className="upload-progress-container">
      <style>{`
        .upload-progress-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 2rem;
          background: linear-gradient(to bottom, #ffffff, #f8fafc);
          width: 100%;
          height: 100%;
          overflow-y: auto;
        }

        .progress-header {
          width: 100%;
          text-align: center;
          margin-bottom: 2rem;
          padding: 0 2rem;
        }

        .progress-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1a365d;
          margin-bottom: 0.5rem;
        }

        .progress-subtitle {
          color: #4a5568;
          font-size: 1rem;
        }

        .status-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          width: 100%;
          margin: 2rem 0;
          padding: 0 2rem;
        }

        .status-card {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s ease;
          border: 1px solid #e2e8f0;
        }

        .status-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
        }

        .status-label {
          font-size: 0.875rem;
          color: #4a5568;
          margin-bottom: 0.5rem;
        }

        .status-value {
          font-size: 1.5rem;
          font-weight: 600;
          color: #2d3748;
        }

        .progress-bar-container {
          width: 100%;
          height: 8px;
          background: #e2e8f0;
          border-radius: 4px;
          overflow: hidden;
          margin: 2rem;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #3182ce, #4299e1);
          transition: width 0.3s ease;
          border-radius: 4px;
          animation: progressPulse 2s infinite;
        }

        .time-elapsed {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #4a5568;
          font-size: 0.875rem;
          margin-top: 1rem;
          padding: 0.5rem 1rem;
          background: white;
          border-radius: 9999px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .time-icon {
          color: #3182ce;
        }

        @keyframes progressPulse {
          0% { opacity: 1; }
          50% { opacity: 0.8; }
          100% { opacity: 1; }
        }

        .processing-list {
          width: 100%;
          margin-top: 2rem;
          padding: 0 2rem;
          max-height: 400px;
          overflow-y: auto;
        }

        .processing-list::-webkit-scrollbar {
          width: 8px;
        }

        .processing-list::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .processing-list::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 4px;
        }

        .processing-list::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        .processing-item {
          background: white;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }

        .processing-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .processing-item-name {
          font-weight: 500;
          color: #2d3748;
        }

        .processing-item-progress {
          color: #3182ce;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .progress-indicator {
          width: 60px;
          height: 4px;
          background: #e2e8f0;
          border-radius: 2px;
          overflow: hidden;
        }

        .progress-indicator-bar {
          height: 100%;
          background: #3182ce;
          transition: width 0.3s ease;
        }
      `}</style>

      <div className="progress-header">
        <h2 className="progress-title">Processing Purchase Orders</h2>
        <p className="progress-subtitle">
          {`${completedItems} of ${totalInvoiceItems.length} files completed`}
        </p>
      </div>

          <CircularProgress
            processedFileSize={processedFileSize}
            totalFileSize={totalFileSize}
            formatFileSize={formatFileSize}
            progressPercentage={progress}
          />

      <div className="status-grid">
        <div className="status-card">
          <div className="status-label">Completed</div>
          <div className="status-value" style={{ color: '#38a169' }}>{completedItems}</div>
        </div>
        <div className="status-card">
          <div className="status-label">Processing</div>
          <div className="status-value" style={{ color: '#3182ce' }}>{processingItems}</div>
        </div>
        <div className="status-card">
          <div className="status-label">Pending</div>
          <div className="status-value" style={{ color: '#718096' }}>{pendingItems}</div>
        </div>
        <div className="status-card">
          <div className="status-label">Failed</div>
          <div className="status-value" style={{ color: '#e53e3e' }}>{failedItems}</div>
        </div>
      </div>

      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="time-elapsed">
        <span className="time-icon">⏱</span>
        <span>Time Elapsed: {formatElapsedTime(timeElapsed)}</span>
      </div>

      {processingItems > 0 && (
        <div className="processing-list">
          {totalInvoiceItems
            .filter(item => item.progress > 0 && item.progress < 100)
            .map((item, index) => (
              <div key={index} className="processing-item">
                <span className="processing-item-name">{item.name}</span>
                <span className="processing-item-progress">{`${Math.round(item.progress)}%`}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default UploadingInvoices;
